import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { catchError, map, take } from 'rxjs/operators';
import { of, Observable, BehaviorSubject, lastValueFrom } from 'rxjs';
import { Network } from '@capacitor/network';
import { TranslateService } from '@ngx-translate/core';
import { PopoverController, AlertController } from '@ionic/angular';
import { PopoverComponent } from '../widgets/popover/popover.component';
import { Store } from '@ngrx/store';
import { loadCountries } from '../utils/states-management/actions/countries.action';
import { LOGOUT } from '../utils/states-management/actions/owner.action';
import { ToastUtils } from '../utils/toast_builder';

@Injectable({
  providedIn: 'root',
})
export class VersionCheckService {
  private lastCheckTime: number = 0;
  private checkInterval: number = 1000 * 60 * 1; // 1 min in milliseconds
  public updateAvailable$ = new BehaviorSubject<boolean>(false);
  public updateType: 'major' | 'minor' | 'patch' | null = null;
  public updateDetails$ = new BehaviorSubject<any>(null);
  private gracePeriodEndTime: number | null = null;
  private updateReqPopOver = null;
  private isAppVersionChecked = false;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private popoverController: PopoverController,
    private alertCtrl: AlertController,
    private store: Store,
    private toastUtils: ToastUtils
  ) {

    // Initialize last check time from storage
    const storedTime = localStorage.getItem('lastVersionCheck');
    if (storedTime) {
      this.lastCheckTime = parseInt(storedTime, 10);
    }

    // Initialize from storage
    this.initializeFromStorage();
  }

  // Public observable for components to subscribe to
  get updateAvailable(): Observable<boolean> {
    return this.updateAvailable$.asObservable();
  }

  // Check if it's time to check for updates
  shouldCheckForUpdates(): boolean {
    const now = Date.now();
    return (now - this.lastCheckTime) > this.checkInterval;
  }

  async getWebAppInfo() {
    const response = await fetch('manifest.webmanifest');
    const manifest = await response.json();
    return {
      name: manifest.name,
      version: localStorage.getItem('validatedAppVersion') || "1.0.0",
      build: manifest.build || '100',
      platform: 'web',
    };
  }

  public async getAppInfo() {
    if (Capacitor.getPlatform() === 'web') {
      return this.getWebAppInfo();
    } else {
      if (localStorage.getItem('validatedAppVersion')) {
        return { version: localStorage.getItem('validatedAppVersion') }
      } else {
        return await App.getInfo();
      }

    }
  }
  async checkVersion(force: boolean = false): Promise<Observable<any>> {
    try {
      // Add cache validation
      const cachedVersion = localStorage.getItem('validatedAppVersion');
      const currentAppInfo = await this.getAppInfo();

      if (!force && cachedVersion === currentAppInfo.version) {
        return of({ updateRequired: false });
      }

      // Skip check if not forced and not time yet
      if (!force && !this.shouldCheckForUpdates()) {
        // console.log('Skipping version check - checked recently');
        return of({ updateRequired: this.updateAvailable$.value });
      }

      // Skip check if offline


      // Check if we're in grace period
      if (this.gracePeriodEndTime && Date.now() < this.gracePeriodEndTime) {
        // console.log('In grace period - skipping forced update');
        return of({
          updateRequired: true,
          gracePeriod: true,
          gracePeriodRemaining: Math.floor((this.gracePeriodEndTime - Date.now()) / (1000 * 60 * 60 * 24))
        });
      }

      const appInfo = currentAppInfo;
      const platform = Capacitor.getPlatform();

      // Update last check time
      this.lastCheckTime = Date.now();
      localStorage.setItem('lastVersionCheck', this.lastCheckTime.toString());

      return this.http.post(`${environment.appRoot}/api/check-version`, {
        platform: platform,
        version: appInfo.version,
        timestamp: new Date().toISOString()
      }).pipe(
        map((response: any) => {
          if (response.update_required) {
            this.updateType = response.update_type;
            this.updateDetails$.next(response);

            // Set grace period if provided
            if (response.grace_period_days > 0) {
              this.gracePeriodEndTime = Date.now() + (response.grace_period_days * 24 * 60 * 60 * 1000);
              localStorage.setItem('gracePeriodEndTime', this.gracePeriodEndTime.toString());
            }

            // Store update info for later use
            localStorage.setItem('pendingUpdateInfo', JSON.stringify({
              type: response.update_type,
              version: response.latest_version,
              forceUpdate: response.force_update,
              forceLogout: response.force_logout_user,
              forceReloadData: response.force_reload_data
            }));
          }
          return response;
        }),
        catchError(error => {
          console.error('Version check failed:', error);
          return of({ updateRequired: false, error: true });
        })
      );
    } catch (error) {
      console.error('Error getting app info:', error);
      return of({ updateRequired: false, error: true });
    }
  }

  /**
   * Main method to check for app updates and handle UI interactions
   */
  async checkAppVersion(force: boolean = false): Promise<void> {
    if (this.isAppVersionChecked && !force) {
      return;
    }

    try {
    const currentVersion = (await this.getAppInfo()).version;
    const storedVersion = localStorage.getItem('validatedAppVersion');

    // Check if we just completed an update
    if (localStorage.getItem('updateInProgress') && currentVersion !== storedVersion) {
      await this.handlePostUpdateActions(currentVersion);
      localStorage.removeItem('updateInProgress');
    }

    // Get update information
    const response: any = await lastValueFrom(
      (await this.checkVersion(force)).pipe(take(1))
    );

    if (response?.update_required) {
      // Determine update message based on update type
      let updateMessage = '';

      switch (response.update_type) {
        case 'major':
          updateMessage = await this.translate.instant('major_update_req', {
            latest_version: response.latest_version,
          });
          break;
        case 'minor':
          updateMessage = await this.translate.instant('minor_update_req', {
            latest_version: response.latest_version,
          });
          break;
        default:
          updateMessage = await this.translate.instant('update_req', {
            latest_version: response.latest_version,
          });
      }

      // Add release notes if available
      if (response.release_notes) {
        updateMessage += `\n\n${response.release_notes}`;
      }

      // Show grace period info if available
      if (response.grace_period_days > 0 && !response.force_update) {
        // Add to translation files:
        // en.json: "update_grace_period": "You have {{days}} days to update the app"
        // fr.json: "update_grace_period": "Vous avez {{days}} jours pour mettre à jour l'application"
        // es.json: "update_grace_period": "Tiene {{days}} días para actualizar la aplicación"
        updateMessage += `\n\n${await this.translate.instant('update_grace_period', {
          days: response.grace_period_days
        })}`;
      }

      let CProps = {
        key1: updateMessage,
        canBeClosed: !response.force_update,
        is_confirm_needed: true,
        confirmLink: response?.update_url,
        onConfirm: () => this.handleAppUpdate(response)
      };
      if (this.updateReqPopOver) {
        this.updateReqPopOver.dismiss();
        this.updateReqPopOver = null;
      }
      if (!this.updateReqPopOver && !this.isAppVersionChecked) {

        this.updateReqPopOver = await this.popoverController.create({
          component: PopoverComponent,
          cssClass: 'popover-custom',
          animated: true,
          componentProps: CProps,
          backdropDismiss: false,
        });

        // Set flag to prevent multiple checks
        this.isAppVersionChecked = true;

        await this.updateReqPopOver.present();
        await this.updateReqPopOver.onWillDismiss();


        // If it's a critical update that was dismissed, schedule a reminder
        if (response.update_type === 'major' && !response.force_update) {
          setTimeout(() => {
            this.isAppVersionChecked = false; // Allow check again
          }, 1000 * 60 * 60); // Remind after 1 hour
        }
      }

      this.updateAvailable$.next(true);

    } else {
      // Mark current version as validated
      localStorage.setItem('validatedAppVersion', currentVersion);
    }
    } catch (error) {
      console.error('Error checking app version:', error);
    }
  }

  /**
   * Handle the app update process
   */
  private async handleAppUpdate(response: any) {
    // console.log("Handling app update:", response.update_type);
    localStorage.setItem('validatedAppVersion', response.latest_version);

    // Set update in progress flag
    localStorage.setItem('updateInProgress', 'true');
    localStorage.setItem('pendingAppVersion', response.latest_version);

    // Handle different update types
    if (response.update_type === 'major' || response.update_type === 'minor') {
      // For major/minor updates, handle data reloading
      if (response?.force_reload_data) {
        this.store.dispatch(loadCountries());
      }

      // For major updates or when forced, handle logout
      if ((response.update_type === 'major' && response?.force_logout_user) ||
        response?.force_logout_user) {
        this.store.dispatch({ type: LOGOUT, redirection_path: '/' });
      }
    }

    // Store the new version

    // For forced updates, clear cache and reload immediately
    if (response?.force_update) {
      // console.log('Performing forced update - clearing caches and reloading');
      await this.clearCachesAndReload();
      return;
    }

    // For non-forced updates, prompt user to reload at their convenience
    if (!response.force_update) {
      // const alert = await this.alertCtrl.create({
      //   header: await this.translate.instant('update_ready'),
      //   message: await this.translate.instant('update_reload_prompt'),
      //   buttons: [
      //     {
      //       text: await this.translate.instant('later'),
      //       role: 'cancel'
      //     },
      //     {
      //       text: await this.translate.instant('reload_now'),
      //       handler: () => {
      //         this.clearCachesAndReload();
      //       }
      //     }
      //   ]
      // });
      let props = {
        key1: 'update_reload_prompt',
        canBeClosed: true,
        is_confirm_needed: true,
        is_info_popover: true,
        is_loader_needed: true,
      };

      let popover = await this.toastUtils.popOverBuilder(
        props,
        PopoverComponent,
        'popover-custom',
        null,
        true,
        true
      );
      await popover.onWillDismiss().then((val: any) => {
        if (val.closed) this.clearCachesAndReload()
      });

      // await alert.present();
    }
  }

  /**
   * Handle post-update actions
   */
  private async handlePostUpdateActions(currentVersion: string): Promise<void> {
    try {
      const pendingVersion = localStorage.getItem('pendingAppVersion');
      const updateInfo = this.getStoredUpdateInfo();

      if (pendingVersion && pendingVersion === currentVersion) {
        // console.log(`Update completed to version ${currentVersion}`);

        // Show update success message
        await this.toastUtils.toastBuilder(
          await this.translate.instant('update_success', { version: currentVersion }),
          'success',
          'bottom',
          'dark',
          3000
        );

        // Execute post-update actions based on update type
        if (updateInfo) {
          if (updateInfo.forceReloadData) {
            this.store.dispatch(loadCountries());
            // Reload other necessary data
          }

          // Clear update info after processing
          localStorage.removeItem('pendingUpdateInfo');
          localStorage.removeItem('pendingAppVersion');
        }

        // Set the new version as validated
        localStorage.setItem('validatedAppVersion', currentVersion);
      }
    } catch (error) {
      console.error('Error handling post-update actions:', error);
    }
  }

  /**
   * Clear caches and reload the application
   */
  private async clearCachesAndReload() {
    // if ('caches' in window) {
    //   try {
    //     const keys = await caches.keys();
    //     await Promise.all(keys.map(key => caches.delete(key)));
    console.log('All caches cleared successfully');
    //   } catch (err) {
    //     console.error('Error clearing caches:', err);
    //   }
    // }
    // // localStorage.clear();

    // // Clear service worker cache if applicable
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations?.length > 0) {
        for (let registration of registrations) {
          registration.unregister();
        }
        // console.log('Service workers unregistered successfully');
      } else {
        // console.log('No Service workers to clear');
      }
    }
    // window.location.href = window.location.href.split('?')[0] +
    //   `?_cache_buster=${Date.now()}&force_reload=true`;
    // Reload the page to ensure fresh cache
    window.location.reload();
  }


  /**
   * Check if update is critical
   */
  isUpdateCritical(): boolean {
    const updateInfo = this.getStoredUpdateInfo();
    return updateInfo?.forceUpdate || false;
  }

  /**
   * Get stored update info
   */
  getStoredUpdateInfo(): any {
    const infoStr = localStorage.getItem('pendingUpdateInfo');
    return infoStr ? JSON.parse(infoStr) : null;
  }

  /**
   * Initialize from storage
   */
  initializeFromStorage(): void {
    const gracePeriodEndTimeStr = localStorage.getItem('gracePeriodEndTime');
    if (gracePeriodEndTimeStr) {
      this.gracePeriodEndTime = parseInt(gracePeriodEndTimeStr, 10);
      // Clear expired grace period
      if (Date.now() > this.gracePeriodEndTime) {
        localStorage.removeItem('gracePeriodEndTime');
        this.gracePeriodEndTime = null;
      }
    }

    // Check for pending updates
    const updateInfo = this.getStoredUpdateInfo();
    if (updateInfo) {
      this.updateType = updateInfo.type;
      this.updateAvailable$.next(true);
    }
  }
}


